/**
 * W1-11b (app-shell-ui): the single 40 px top bar (DESIGN.md §4.4): project menu and title | workspace tabs (F2 / F3
 * / F4, also buttons, since many laptops need Fn for F-keys) | transport | snap | undo / redo with the action's name |
 * status chips | palette | level. Below 1536 px the level menu moves into the overflow menu and the save chip keeps
 * only its icon (UX-011: the less-used parts go first), below 1280 px the snap controls, below 1180 px the status
 * chips, below 1100 px undo / redo follow, in that order; the row never wraps and the title truncates. The
 * overflow button carries a dot when something hidden in it needs attention (not saved, errors); the collapsed chips
 * become its rows (problems, save state, changed since export, level), while the status items other modules register
 * for 'topbar.right' stay in the bar (they are components, not menu rows). Chrome buttons do
 * not take focus on pointer down (`data-chrome`), so Space keeps playing after a click. Panels other modules register
 * for the global slots sit inline: `topbar.left` after the project menu, `topbar.center` after the transport,
 * `topbar.right` before undo / redo.
 */

import type { MessageRef } from '../../core/util/types';
import type { WorkspaceId } from '../../state/types';
import { Icon } from '../../ui/Icon';
import { IconButton } from '../../ui/IconButton';
import { Menu } from '../../ui/Menu';
import { Tooltip } from '../../ui/Tooltip';
import { Kbd } from '../../ui/Kbd';
import type { MenuItem } from '../../ui/types';
import type { AppServices } from '../registry/types';
import type { ShellUiService } from '../uiService';
import { chordsFor } from './commandState';
import { topBarCollapse } from './LayoutPresets';
import { ProjectMenu } from './ProjectMenu';
import { problemsPanelId } from './shellCommands';
import {
  useEditorState,
  useHistorySummary,
  useKeymapVersion,
  useLayoutViewport,
  useSettingsValue,
  useStore,
  useText,
  type ShellText,
} from './ShellContext';
import { InlineSlot } from './SlotHost';
import { SnapControls, snapLabel } from './SnapControls';
import {
  downloadBackup,
  LevelChip,
  ModuleStatusItems,
  openExport,
  saveIcon,
  saveLabel,
  saveNeedsAttention,
  saveStateOf,
  StatusChips,
  useExportState,
  useFirstPassPending,
  useProblemCounts,
  useStorageHealth,
} from './StatusChips';
import { TransportBar } from './TransportBar';
import { SNAP_PRESETS } from './playback';
import { m } from './shell.messages';
import s from './Shell.module.css';

const WORKSPACES: ReadonlyArray<{
  readonly id: WorkspaceId;
  readonly short: typeof m.wsChart;
  readonly full: typeof m.wsChart;
  readonly icon: string;
}> = [
  { id: 'timing', short: m.wsTimingShort, full: m.wsTiming, icon: 'AudioLines' },
  { id: 'chart', short: m.wsChart, full: m.wsChart, icon: 'Music4' },
  { id: 'publish', short: m.wsPublish, full: m.wsPublish, icon: 'PackageCheck' },
];

function WorkspaceTabs(props: { readonly services: AppServices }) {
  const { services } = props;
  const text = useText(services);
  const workspace = useEditorState(services, (st) => st.workspace);
  useKeymapVersion(services);
  const bindings = services.keymap.bindings();
  return (
    <nav className={s.workspaceTabs} aria-label={text.t(m.workspacesLabel)}>
      {WORKSPACES.map((w) => {
        const chord = chordsFor(bindings, 'app.workspace', { workspace: w.id })[0];
        const current = w.id === workspace;
        return (
          <Tooltip
            key={w.id}
            content={
              <span className={s.tooltipRow}>
                {text.t(w.full)}
                {chord !== undefined && <Kbd chord={chord} />}
              </span>
            }
            side="bottom"
          >
            <button
              type="button"
              className={s.workspaceTab}
              aria-current={current ? 'page' : undefined}
              data-workspace={w.id}
              data-help-id={`workspace.${w.id}`}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={() => {
                if (!current) void services.registry.run('app.workspace', { workspace: w.id }, 'button');
              }}
            >
              {text.t(w.short)}
            </button>
          </Tooltip>
        );
      })}
    </nav>
  );
}

function UndoRedo(props: { readonly services: AppServices; readonly text: ShellText }) {
  const { services, text } = props;
  const store = useStore(services);
  const h = useHistorySummary(store);
  const label = (base: typeof m.cmdUndo, withLabel: typeof m.undoWith, entry: MessageRef | null): MessageRef =>
    entry === null ? { key: base } : { key: withLabel, params: { label: text.tr(entry) } };
  return (
    <div className={s.undoRedo} role="group">
      <IconButton
        icon="Undo2"
        label={label(m.cmdUndo, m.undoWith, h.canUndo ? h.undoLabel : null)}
        command="edit.undo"
      />
      <IconButton
        icon="Redo2"
        label={label(m.cmdRedo, m.redoWith, h.canRedo ? h.redoLabel : null)}
        command="edit.redo"
      />
    </div>
  );
}

/** Items of the overflow menu for the groups that do not fit. */
function OverflowMenu(props: {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  readonly collapse: ReturnType<typeof topBarCollapse>;
}) {
  const { services, ui, collapse } = props;
  const text = useText(services);
  const snap = useEditorState(services, (st) => st.snap);
  const level = useSettingsValue(services).general.level;
  const counts = useProblemCounts(services.problems);
  const checking = useFirstPassPending(services);
  const health = useStorageHealth(services);
  const exportState = useExportState(services);
  const save = saveStateOf(health, false);
  const items: MenuItem[] = [];
  if (collapse.snap) {
    items.push({
      kind: 'submenu',
      label: text.t(m.labelValue, { label: text.t(m.menuSnap), value: snapLabel(snap.div, text) }),
      items: [
        ...SNAP_PRESETS.map((d): MenuItem => ({
          kind: 'action',
          label: snapLabel(d, text),
          ...(d === snap.div ? { icon: 'Check' } : {}),
          onSelect: () => {
            services.editor.getState().setSnap({ div: d });
          },
        })),
        { kind: 'separator' },
        { kind: 'command', command: 'snap.toggleBeat' },
        { kind: 'command', command: 'snap.toggleX' },
      ],
    });
  }
  if (collapse.undo) {
    if (items.length > 0) items.push({ kind: 'separator' });
    items.push({ kind: 'command', command: 'edit.undo' }, { kind: 'command', command: 'edit.redo' });
  }
  if (collapse.chips) {
    if (items.length > 0) items.push({ kind: 'separator' });
    const panel = problemsPanelId(services);
    const total = counts.fatal + counts.error + counts.warning + counts.tip;
    items.push({
      kind: 'action',
      icon: counts.fatal + counts.error > 0 ? 'CircleX' : 'TriangleAlert',
      label: checking
        ? text.t(m.problemsChecking)
        : total === 0
          ? text.t(m.problemsNone)
          : text.t(m.problemsChip, {
              fatal: counts.fatal,
              errors: counts.error,
              warnings: counts.warning,
              tips: counts.tip,
            }),
      disabledReason: panel === null ? { key: m.reasonNotHere } : null,
      onSelect: () => {
        if (panel !== null) ui.showPanel(panel);
      },
    });
    // Not saved / at risk: choosing it offers the backup (DESIGN.md §5.13 "Download backup now").
    items.push({
      kind: 'action',
      icon: saveIcon(save),
      label: saveNeedsAttention(save)
        ? text.t(m.labelValue, { label: saveLabel(save, text), value: text.t(m.downloadBackup) })
        : saveLabel(save, text),
      onSelect: () => {
        if (saveNeedsAttention(save)) downloadBackup(services);
      },
    });
    // "Changed since export" (DESIGN.md §4.4): opens the export dialog.
    if (exportState?.changed === true) {
      items.push({
        kind: 'action',
        icon: 'CircleDot',
        label: exportState.label,
        disabledReason: exportState.canExport ? null : { key: m.reasonNotHere },
        onSelect: () => {
          openExport(services);
        },
      });
    }
  }
  if (collapse.level) {
    if (items.length > 0) items.push({ kind: 'separator' });
    items.push({
      kind: 'submenu',
      label: text.t(m.levelLabel, { level }),
      items: (['guided', 'full'] as const).map((l): MenuItem => ({
        kind: 'action',
        label: text.t(l === 'guided' ? m.levelGuided : m.levelFull),
        ...(l === level ? { icon: 'Check' } : {}),
        onSelect: () => {
          services.settings.set('general', 'level', l);
          services.editor.getState().setLevel(l);
        },
      })),
    });
  }
  const attention = (collapse.chips && (saveNeedsAttention(save) || counts.fatal + counts.error > 0)) || false;
  if (items.length === 0) return null;
  return (
    <Menu
      align="end"
      label={text.t(m.overflow)}
      trigger={
        <button
          type="button"
          className={s.overflowButton}
          aria-label={text.t(attention ? m.overflowAttention : m.overflow)}
          data-attention={attention ? 'true' : undefined}
        >
          <Icon name="Ellipsis" size={16} />
        </button>
      }
      items={items}
    />
  );
}

export function TopBar(props: { readonly services: AppServices; readonly ui: ShellUiService }) {
  const { services, ui } = props;
  const text = useText(services);
  const viewport = useLayoutViewport(services);
  const collapse = topBarCollapse(viewport.width);
  return (
    <header
      className={s.topBar}
      aria-label={text.t(m.topBarLabel)}
      data-chrome=""
      data-collapse={
        [
          collapse.level ? 'level' : '',
          collapse.snap ? 'snap' : '',
          collapse.chips ? 'chips' : '',
          collapse.undo ? 'undo' : '',
        ]
          .filter(Boolean)
          .join(' ') || undefined
      }
    >
      <div className={s.topLeft}>
        <ProjectMenu services={services} />
        <InlineSlot services={services} slot="topbar.left" />
      </div>
      <span className={s.divider} aria-hidden="true" />
      <WorkspaceTabs services={services} />
      <span className={s.divider} aria-hidden="true" />
      <TransportBar services={services} />
      <InlineSlot services={services} slot="topbar.center" />
      {!collapse.snap && (
        <>
          <span className={s.divider} aria-hidden="true" />
          <SnapControls services={services} />
        </>
      )}
      <span className={s.spacer} />
      <InlineSlot services={services} slot="topbar.right" />
      {!collapse.undo && <UndoRedo services={services} text={text} />}
      {!collapse.chips && <StatusChips services={services} ui={ui} compact={collapse.chipText} />}
      {collapse.chips && <ModuleStatusItems services={services} />}
      <IconButton icon="Search" label={{ key: m.paletteButton }} command="app.palette" />
      {!collapse.level && <LevelChip services={services} />}
      <OverflowMenu services={services} ui={ui} collapse={collapse} />
    </header>
  );
}
